import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

interface Ticket {
  id: number;
  title: string;
  description: string;
  price: number;
  image: string;
  duration: string;
  meetingPoint: string;
  maxParticipants: number;
  availableDates: string[];
  availableTimes: string[];
  category: string;
}

const TicketsPage: React.FC = () => {
  const { translations } = useLanguage();
  const [cart, setCart] = useState<{[key: number]: number}>({});
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [showModal, setShowModal] = useState(false);

  const tickets: Ticket[] = [
    {
      id: 1,
      title: "花蓮一日遊",
      description: "探索花蓮最精華的景點，包含太魯閣峽谷、清水斷崖、七星潭等知名景點。專業導遊帶領，深入了解花蓮的自然與人文風情。",
      price: 1200,
      image: "https://images.unsplash.com/photo-1551632811-561732d1e306?w=400&h=300&fit=crop",
      duration: "8小時",
      meetingPoint: "花蓮火車站",
      maxParticipants: 20,
      availableDates: ["2024-01-15", "2024-01-16", "2024-01-17"],
      availableTimes: ["08:00", "09:00"],
      category: "一日遊"
    },
    {
      id: 2,
      title: "清水斷崖泛舟",
      description: "在太平洋上體驗刺激的泛舟活動，欣賞清水斷崖的壯麗景色。適合喜歡冒險的旅客，提供完整的安全裝備。",
      price: 1800,
      image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop",
      duration: "4小時",
      meetingPoint: "清水斷崖遊客中心",
      maxParticipants: 12,
      availableDates: ["2024-01-15", "2024-01-16"],
      availableTimes: ["09:00", "14:00"],
      category: "戶外活動"
    },
    {
      id: 3,
      title: "賞鯨體驗",
      description: "搭乘專業賞鯨船出海，尋找太平洋中的鯨魚蹤跡。季節限定活動，提供專業解說員介紹海洋生態。",
      price: 1500,
      image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop",
      duration: "3小時",
      meetingPoint: "花蓮港",
      maxParticipants: 30,
      availableDates: ["2024-01-15", "2024-01-16", "2024-01-17"],
      availableTimes: ["08:30", "13:30"],
      category: "海洋活動"
    },
    {
      id: 4,
      title: "原住民文化體驗",
      description: "深入了解阿美族文化，體驗傳統手工藝製作、品嚐原住民美食，參與傳統歌舞表演。",
      price: 800,
      image: "https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=400&h=300&fit=crop",
      duration: "2小時",
      meetingPoint: "阿美族文化村",
      maxParticipants: 15,
      availableDates: ["2024-01-15", "2024-01-16"],
      availableTimes: ["10:00", "14:00", "16:00"],
      category: "文化體驗"
    },
    {
      id: 5,
      title: "太魯閣健行",
      description: "專業導遊帶領的太魯閣峽谷健行，探索燕子口、九曲洞等著名景點，了解地質形成過程。",
      price: 1000,
      image: "https://images.unsplash.com/photo-1551632811-561732d1e306?w=400&h=300&fit=crop",
      duration: "6小時",
      meetingPoint: "太魯閣遊客中心",
      maxParticipants: 18,
      availableDates: ["2024-01-15", "2024-01-16", "2024-01-17"],
      availableTimes: ["08:00", "09:00"],
      category: "健行"
    },
    {
      id: 6,
      title: "手工皂DIY",
      description: "使用花蓮在地天然材料製作手工皂，學習傳統製皂技術，帶回獨一無二的紀念品。",
      price: 600,
      image: "https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=400&h=300&fit=crop",
      duration: "1.5小時",
      meetingPoint: "花蓮文創園區",
      maxParticipants: 10,
      availableDates: ["2024-01-15", "2024-01-16"],
      availableTimes: ["10:00", "14:00", "16:00"],
      category: "DIY體驗"
    }
  ];

  const addToCart = (ticketId: number) => {
    setCart(prev => ({
      ...prev,
      [ticketId]: (prev[ticketId] || 0) + 1
    }));
  };

  const removeFromCart = (ticketId: number) => {
    setCart(prev => {
      const newCart = { ...prev };
      if (newCart[ticketId] > 1) {
        newCart[ticketId] -= 1;
      } else {
        delete newCart[ticketId];
      }
      return newCart;
    });
  };

  const getCartTotal = () => {
    return Object.entries(cart).reduce((total, [ticketId, quantity]) => {
      const ticket = tickets.find(t => t.id === parseInt(ticketId));
      return total + (ticket?.price || 0) * quantity;
    }, 0);
  };

  const openBookingModal = (ticket: Ticket) => {
    setSelectedTicket(ticket);
    setShowModal(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8 text-center">
          {translations.tickets.title}
        </h1>

        {/* Cart Summary */}
        {Object.keys(cart).length > 0 && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <h2 className="text-lg font-semibold mb-4">購物車</h2>
            <div className="space-y-2">
              {Object.entries(cart).map(([ticketId, quantity]) => {
                const ticket = tickets.find(t => t.id === parseInt(ticketId));
                return ticket ? (
                  <div key={ticketId} className="flex justify-between items-center">
                    <span>{ticket.title} x {quantity}</span>
                    <div className="flex items-center space-x-2">
                      <span>NT$ {ticket.price * quantity}</span>
                      <button
                        onClick={() => removeFromCart(ticket.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ) : null;
              })}
              <div className="border-t pt-2 mt-4">
                <div className="flex justify-between items-center font-semibold">
                  <span>總計</span>
                  <span>NT$ {getCartTotal()}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tickets Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tickets.map((ticket) => (
            <div key={ticket.id} className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="relative h-48">
                <img
                  src={ticket.image}
                  alt={ticket.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 right-4 bg-white px-3 py-1 rounded-full text-sm font-semibold text-hualien-blue">
                  NT$ {ticket.price}
                </div>
              </div>
              <div className="p-6">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-xl font-semibold text-gray-900">{ticket.title}</h3>
                  <span className="text-sm text-gray-500">{ticket.category}</span>
                </div>
                <p className="text-gray-600 mb-4 line-clamp-3">{ticket.description}</p>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="mr-2">⏱️</span>
                    <span>{ticket.duration}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="mr-2">📍</span>
                    <span>{ticket.meetingPoint}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="mr-2">👥</span>
                    <span>最多 {ticket.maxParticipants} 人</span>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => addToCart(ticket.id)}
                    className="flex-1 bg-hualien-blue text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                  >
                    {translations.tickets.addToCart}
                  </button>
                  <button
                    onClick={() => openBookingModal(ticket)}
                    className="flex-1 bg-white border border-hualien-blue text-hualien-blue py-2 px-4 rounded-lg font-medium hover:bg-hualien-light-blue transition-colors"
                  >
                    {translations.tickets.bookNow}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Booking Modal */}
        {showModal && selectedTicket && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
              <h3 className="text-xl font-semibold mb-4">{selectedTicket.title}</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {translations.tickets.selectDate}
                  </label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                    {selectedTicket.availableDates.map(date => (
                      <option key={date} value={date}>{date}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {translations.tickets.selectTime}
                  </label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                    {selectedTicket.availableTimes.map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {translations.tickets.participants}
                  </label>
                  <input
                    type="number"
                    min="1"
                    max={selectedTicket.maxParticipants}
                    defaultValue="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
              <div className="flex space-x-2 mt-6">
                <button
                  onClick={() => setShowModal(false)}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-gray-400 transition-colors"
                >
                  {translations.common.cancel}
                </button>
                <button
                  onClick={() => {
                    alert('預約成功！');
                    setShowModal(false);
                  }}
                  className="flex-1 bg-hualien-blue text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  {translations.tickets.bookNow}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketsPage; 